import { useMemo } from 'react';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { BlokIskeleti } from '../satis-analiz/BlokIskeleti';
import { miktarMetni, oranMetni } from '../satis-analiz/bicim';
import type { StokKaynaklari } from './ekranKaynaklari';
import { kategoriSuz } from './kategori';
import { KartHatasi } from './KartHatasi';
import { stokDagilimi, type EnvanterSatiri, type OluStokSatiri } from './toplama';
import { useStokSorgu, type StokSorguFiltresi } from './useStokVerisi';

const ILK_N = 10;

interface Props {
  kaynaklar: StokKaynaklari;
  f: StokSorguFiltresi;
  /** Bu ds'ler @KATEGORI bildirmez; secim istemcide uygulanir. */
  kategori: string | null;
}

/** Kategori bazinda stok degeri ve icindeki olu stok (990023 + 990077, sayfa anahtarlari; ek istek yok). */
export function StokDagilimBlok({ kaynaklar, f, kategori }: Props) {
  const envQ = useStokSorgu<EnvanterSatiri>(kaynaklar.envanter, f);
  const oluQ = useStokSorgu<OluStokSatiri>(kaynaklar.oluStokTur, f);
  const satirlar = useMemo(
    () => stokDagilimi(kategoriSuz(envQ.data?.data ?? [], kategori), kategoriSuz(oluQ.data?.data ?? [], kategori)),
    [envQ.data, oluQ.data, kategori],
  );

  if (!kaynaklar.envanter) return null;
  if (envQ.isLoading || oluQ.isLoading) {
    return <BlokIskeleti testid="sermaye-dagilim-iskelet" baslik="Stok nerede duruyor" yukseklik={320} satir={8} />;
  }
  if (envQ.isError) {
    return <KartHatasi testid="sermaye-dagilim-hata" baslik="Stok nerede duruyor" yukseklik={200} mesaj="Stok dağılımı yüklenemedi." />;
  }
  if (satirlar.length === 0) return null;

  return (
    <div data-testid="sermaye-dagilim">
      <SectionCard title="Stok nerede duruyor" subtitle="Kategoriye göre eldeki stok ve içindeki ölü stok (bugün)">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-[12px] text-ink-500 border-b border-border">
                <th className="text-left font-medium py-2 pr-3">Kategori</th>
                <th className="text-right font-medium py-2 px-3">Ürün</th>
                <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Stok değeri</th>
                <th className="text-left font-medium py-2 px-3">Pay</th>
                <th className="text-right font-medium py-2 px-3">Ölü stok</th>
                <th className="text-right font-medium py-2 pl-3 whitespace-nowrap">Ölü payı</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map((s) => (
                <tr key={s.kategori} data-testid={`sermaye-dagilim-${s.kategori}`} className="border-b border-border last:border-b-0">
                  <td className="py-2 pr-3 font-medium text-ink-900 whitespace-nowrap">{s.kategori}</td>
                  <td className="py-2 px-3 text-right tnum text-ink-500">{fmtNum(s.urun)}</td>
                  <td className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(s.deger)}</td>
                  <td className="py-2 px-3 min-w-[120px]">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded-full bg-border-light overflow-hidden">
                        <div className="h-full rounded-full bg-ink-700" style={{ width: `${Math.min(100, Math.max(0, s.pay ?? 0))}%` }} />
                      </div>
                      <span className="tnum text-[12px] text-ink-500 w-11 text-right">{s.pay === null ? '-' : oranMetni(s.pay, 1)}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(s.oluDeger)}</td>
                  <td className="py-2 pl-3 text-right tnum text-ink-500">{s.oluPay === null ? '-' : oranMetni(s.oluPay, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

/** En cok para baglanan urunler (990023) ve en degerli olu stok (990077). Ek istek yok. */
export function UrunSermayeBlok({ kaynaklar, f, kategori }: Props) {
  const envQ = useStokSorgu<EnvanterSatiri>(kaynaklar.envanter, f);
  const oluQ = useStokSorgu<OluStokSatiri>(kaynaklar.oluStokTur, f);
  const enDegerli = useMemo(
    () =>
      [...kategoriSuz(envQ.data?.data ?? [], kategori)]
        .sort((a, b) => Number(b.envanter_degeri) - Number(a.envanter_degeri))
        .slice(0, ILK_N),
    [envQ.data, kategori],
  );
  const enDegerliOlu = useMemo(
    () => [...kategoriSuz(oluQ.data?.data ?? [], kategori)].sort((a, b) => Number(b.deger) - Number(a.deger)).slice(0, ILK_N),
    [oluQ.data, kategori],
  );

  if (enDegerli.length === 0 && enDegerliOlu.length === 0) return null;
  return (
    <div data-testid="sermaye-urunler" className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
      {enDegerli.length > 0 && (
        <div data-testid="sermaye-en-degerli" className="min-w-0 flex flex-col">
          <SectionCard className="flex-1" title="En çok para bağlanan ürünler" subtitle={`Stok değerine göre ilk ${enDegerli.length}`}>
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-ink-400 text-left">
                  <th className="font-normal py-1">Ürün</th>
                  <th className="font-normal py-1 text-right">Eldeki</th>
                  <th className="font-normal py-1 pl-2 text-right">Değer</th>
                </tr>
              </thead>
              <tbody>
                {enDegerli.map((s) => (
                  <tr key={s.stok_kodu} className="border-t border-border">
                    <td className="py-1.5 pr-2 text-ink-800 truncate max-w-0 w-full">{s.urun}</td>
                    <td className="py-1.5 text-right tnum text-ink-500 whitespace-nowrap">{miktarMetni(Number(s.eldeki_miktar) || 0)}</td>
                    <td className="py-1.5 pl-2 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(Number(s.envanter_degeri) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        </div>
      )}
      {enDegerliOlu.length > 0 && (
        <div data-testid="sermaye-en-degerli-olu" className="min-w-0 flex flex-col">
          <SectionCard className="flex-1" title="En değerli ölü stok" subtitle={`Değere göre ilk ${enDegerliOlu.length}`}>
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-ink-400 text-left">
                  <th className="font-normal py-1">Ürün</th>
                  <th className="font-normal py-1 text-right whitespace-nowrap">Satmayan gün</th>
                  <th className="font-normal py-1 pl-2 text-right">Değer</th>
                </tr>
              </thead>
              <tbody>
                {enDegerliOlu.map((s) => (
                  <tr key={s.stok_kod} className="border-t border-border">
                    <td className="py-1.5 pr-2 text-ink-800 truncate max-w-0 w-full">{s.stok_ad}</td>
                    <td className="py-1.5 text-right tnum text-ink-500 whitespace-nowrap">{fmtNum(Number(s.gun) || 0)}</td>
                    <td className="py-1.5 pl-2 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(Number(s.deger) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
